"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { MapPin, Loader2, Copy, Check } from "lucide-react"
import { toast } from "sonner"
import dynamic from "next/dynamic"

// Créer un wrapper pour le composant Map
const MapWrapper = dynamic(() => import("./geolocation-map").then((mod) => mod.GeolocationMap), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  ),
})

interface GeolocationPickerProps {
  value: string
  onChange: (url: string, lat?: number, lng?: number) => void
}

export function GeolocationPicker({ value, onChange }: GeolocationPickerProps) {
  // Extract coordinates from Google Maps URL (déclaré en haut pour l'initialisation)
  const extractCoordinates = (url: string): { lat: number; lng: number } | null => {
    try {
      const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/)
      if (qMatch) {
        return { lat: Number.parseFloat(qMatch[1]), lng: Number.parseFloat(qMatch[2]) }
      }
      const atMatch = url.match(/@(-?\d+\.?\d+),(-?\d+\.?\d+)/)
      if (atMatch) {
        return { lat: Number.parseFloat(atMatch[1]), lng: Number.parseFloat(atMatch[2]) }
      }
      return null
    } catch {
      return null
    }
  }

  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [urlInput, setUrlInput] = useState(value)
  const [isPickingLocation, setIsPickingLocation] = useState(false)
  const [selectedPos, setSelectedPos] = useState<{ lat: number; lng: number } | null>(() =>
    extractCoordinates(value),
  )

  // Get user's current location
  const getCurrentLocation = async () => {
    setIsPickingLocation(false)
    setLoading(true)
    try {
      if (!navigator.geolocation) {
        toast.error("Géolocalisation non supportée par ce navigateur")
        setLoading(false)
        return
      }

      // Vérifier contexte sécurisé (HTTPS ou localhost)
      if (!window.isSecureContext) {
        console.debug("Insecure context:", location.href)
        toast.error("La géolocalisation nécessite un contexte sécurisé (HTTPS ou localhost).")
        setLoading(false)
        return
      }

      // Détecter navigateur intégré (webview) qui bloque souvent la géoloc
      const ua = navigator.userAgent || ""
      const isInAppBrowser = /FBAN|FBAV|Instagram|Line|WebView/i.test(ua)
      if (isInAppBrowser) {
        console.debug("In-app browser detected:", ua)
        toast.error(
          "Navigateur intégré détecté — la géolocalisation peut être bloquée. Ouvrez la page dans Safari/Chrome et réessayez.",
        )
        setLoading(false)
        return
      }

      // Vérifier l'état de permission si possible
      try {
        if (navigator.permissions && (navigator.permissions as any).query) {
          const status = await (navigator.permissions as any).query({ name: "geolocation" })
          console.debug("Geolocation permission state:", status.state)
          if (status.state === "denied") {
            // Message plus explicite selon plateforme
            const isiOS = /iP(hone|ad|od)/i.test(ua)
            const isAndroid = /Android/i.test(ua)
            if (isiOS) {
              toast.error(
                "Permission de géolocalisation refusée. Allez dans Réglages → Safari → Localisation et autorisez l'accès pour ce site.",
              )
            } else if (isAndroid) {
              toast.error(
                "Permission de géolocalisation refusée. Ouvrez le cadenas dans la barre d'adresse → Paramètres du site → Localisation → Autoriser.",
              )
            } else {
              toast.error(
                "Permission de géolocalisation refusée. Vérifiez les paramètres du site dans votre navigateur.",
              )
            }
            setLoading(false)
            return
          }
          // si 'prompt' ou 'granted' on continue et getCurrentPosition déclenchera l'invite si nécessaire
        }
      } catch (permErr) {
        console.debug("Permissions API non disponible / erreur:", permErr)
        // continuer vers getCurrentPosition
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const mapsUrl = `https://maps.google.com/?q=${latitude},${longitude}`
          setUrlInput(mapsUrl)
          setSelectedPos({ lat: latitude, lng: longitude })
          onChange(mapsUrl, latitude, longitude)
          toast.success("Position actuelle capturée")
          setLoading(false)
        },
        (err) => {
          console.debug("Geolocation error:", err)
          // Géolocalisation renvoie des codes numériques : 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
          if (err && typeof err.code === "number") {
            if (err.code === 1) {
              toast.error(
                "Permission refusée. Vérifiez les permissions du navigateur (icône cadenas → Paramètres du site) ou les réglages système.",
              )
            } else if (err.code === 2) {
              toast.error("Position indisponible. Vérifiez que le GPS est activé et réessayez.")
            } else if (err.code === 3) {
              toast.error("La géolocalisation a expiré. Réessayez.")
            } else {
              toast.error("Impossible d'accéder à la géolocalisation. Vérifiez les permissions.")
            }
          } else {
            toast.error("Impossible d'accéder à la géolocalisation. Vérifiez les permissions.")
          }
          setLoading(false)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      )
    } catch (error) {
      console.error("getCurrentLocation unexpected error:", error)
      toast.error("Erreur lors de la récupération de la géolocalisation")
      setLoading(false)
    }
  }

  // Handle URL input change
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value
    setUrlInput(newUrl)
    const coords = extractCoordinates(newUrl)
    if (coords) {
      setSelectedPos(coords)
      onChange(newUrl, coords.lat, coords.lng)
    } else if (newUrl === "") {
      setSelectedPos(null)
      onChange("", undefined, undefined)
    }
  }

  // Copy URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(urlInput)
    setCopied(true)
    toast.success("Lien copié!")
    setTimeout(() => setCopied(false), 2000)
  }

  // Iframe preview helper (reste inchangé)
  const getEmbedUrl = (): string | null => {
    if (!urlInput) return null
    const coords = extractCoordinates(urlInput)
    if (coords) {
      return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${coords.lng}!3d${coords.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM0MwJzAwLjAiTiAwwrAwJzAwLjAiRQ!5e0!3m2!1sfr!2sfr!4v1234567890`
    }
    return null
  }

  const handleMapSelect = (lat: number, lng: number) => {
    const mapsUrl = `https://maps.google.com/?q=${lat},${lng}`
    setUrlInput(mapsUrl)
    setSelectedPos({ lat, lng })
    onChange(mapsUrl, lat, lng)
    toast.success("Position sélectionnée")
    // laisser isPickingLocation actif pour permettre plusieurs clics si besoin
  }

  // Centre par défaut (Ouagadougou) ou position sélectionnée
  const center = selectedPos ?? { lat: 12.3714, lng: -1.5197 }

  return (
    <div className="space-y-4">
      {/* Buttons */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <MapPin size={18} />}
          {loading ? "Chargement..." : "Ma Position"}
        </button>

        <button
          type="button"
          onClick={() => {
            setIsPickingLocation(!isPickingLocation)
          }}
          className={`px-4 py-2 rounded-lg transition ${isPickingLocation
            ? "bg-warning text-white"
            : "bg-background-secondary border border-border hover:bg-background-secondary/80"
            }`}
        >
          {isPickingLocation ? "Mode sélection actif" : "Sélectionner sur carte"}
        </button>
      </div>

      {/* Leaflet interactive picker */}
      {isPickingLocation && (
        <div className="w-full h-64 rounded-lg overflow-hidden border border-border dark:border-dark-border">
          <MapWrapper center={center} selectedPos={selectedPos} onMapClick={handleMapSelect} />
        </div>
      )}

      {/* URL Input Field */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground dark:text-dark-foreground">URL Google Maps</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={handleUrlChange}
            placeholder="https://maps.google.com/?q=48.8566,2.3522"
            className="input-field flex-1"
          />
          {urlInput && (
            <button
              type="button"
              onClick={copyToClipboard}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition flex items-center gap-2"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
          )}
        </div>
      </div>

      {/* Google Maps Preview */}
      {!isPickingLocation && urlInput && extractCoordinates(urlInput) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground dark:text-dark-foreground">
            Aperçu de la carte
          </label>
          <div className="w-full h-64 rounded-lg overflow-hidden border border-border dark:border-dark-border bg-gray-100">
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              src={`https://maps.google.com/maps?q=${extractCoordinates(urlInput)?.lat},${extractCoordinates(urlInput)?.lng}&z=15&output=embed`}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>
        </div>
      )}

      {/* Info message */}
      {!urlInput && (
        <div className="p-3 bg-info/20 text-info rounded-lg text-sm">
          Cliquez sur "Ma Position" pour capturer votre localisation, ou activez "Sélectionner sur carte" pour choisir manuellement.
        </div>
      )}
    </div>
  )
}
